import { supabase } from '@/lib/supabase/client'

export interface CepCoverageResult {
  covered: boolean
  city: string | null
  state: string | null
  found: boolean
}

export const checkCepCoverage = async (
  cep: string,
): Promise<CepCoverageResult> => {
  const cleanCep = cep.replace(/\D/g, '')
  const formattedCep = cleanCep.replace(/^(\d{5})(\d)/, '$1-$2')

  try {
    // 1. Try Exact Match (Unico)
    // We check both clean and formatted versions to be safe regarding DB storage format
    // Updated type check from 'CEP Único' to 'Unico' as per requirements
    const { data: exactData, error: exactError } = await supabase
      .from('VerificaCEPs')
      .select('ATENDE, Municipio, ESTADO, "CEP Inicial", "CEP Final", Tipo')
      .eq('Tipo', 'Unico')
      .or(`"CEP Inicial".eq.${cleanCep},"CEP Inicial".eq.${formattedCep}`)
      .maybeSingle()

    if (exactError) throw exactError

    if (exactData) {
      return {
        covered: exactData.ATENDE === 'SIM',
        city: exactData.Municipio,
        state: exactData.ESTADO,
        found: true,
      }
    }

    // 2. Try Range Match (Logradouro)
    // For ranges, we assume the DB allows string comparison.
    // We prioritize the clean version (8 digits) for numerical-like comparison.
    let { data: rangeData, error: rangeError } = await supabase
      .from('VerificaCEPs')
      .select('ATENDE, Municipio, ESTADO, "CEP Inicial", "CEP Final", Tipo')
      .eq('Tipo', 'Logradouro')
      .lte('CEP Inicial', cleanCep)
      .gte('CEP Final', cleanCep)
      .maybeSingle()

    // If not found with clean, try formatted version as fallback
    if (!rangeData && !rangeError) {
      const { data: rangeDataFormatted, error: rangeErrorFormatted } =
        await supabase
          .from('VerificaCEPs')
          .select('ATENDE, Municipio, ESTADO, "CEP Inicial", "CEP Final", Tipo')
          .eq('Tipo', 'Logradouro')
          .lte('CEP Inicial', formattedCep)
          .gte('CEP Final', formattedCep)
          .maybeSingle()

      if (rangeErrorFormatted) {
        console.error('Error checking formatted range:', rangeErrorFormatted)
      } else {
        rangeData = rangeDataFormatted
      }
    }

    if (rangeError) throw rangeError

    if (rangeData) {
      return {
        covered: rangeData.ATENDE === 'SIM',
        city: rangeData.Municipio,
        state: rangeData.ESTADO,
        found: true,
      }
    }

    // 3. No match found
    return {
      covered: false,
      city: null,
      state: null,
      found: false,
    }
  } catch (error) {
    console.error('Error checking CEP coverage:', error)
    // Default to not covered on error, but allow user to submit lead
    return {
      covered: false,
      city: null,
      state: null,
      found: false,
    }
  }
}
